<div class="flex flex-nowrap">
    <h2 i18n="@@calculator-title">Exchange rate calculator</h2>
    <a
        href="#"
        class="riege-info"
        matomoClickCategory="Dialog"
        matomoClickAction="Show dialog"
        matomoClickName="Exchange rate calculator"
        aria-label="Information about Exchange rate calculator"
        i18n-aria-label="@@launch-dialog-button-calculator-label"
        (click)="openDialog($event)">
        <mat-icon class="riege-icon md-20">info</mat-icon>
    </a>
</div>

<ng-template #dialogTemplate>
    <mat-dialog-content>
        <h2 mat-dialog-title i18n="@@launch-dialog-calculator-title">Exchange rate calculator information</h2>
        <p i18n="@@launch-dialog-calculator-text"
            >The exchange rate calculator supports exchange rates from different providers. The selection of the
            exchange rate provider determines the currency for the amount to be exchanged. The list of foreign
            currencies is also determined by the exchange rate provider. The calculator uses the daily exchange rates
            for conversion.</p
        >
    </mat-dialog-content>
    <mat-dialog-actions align="end">
        <button
            class="riege-button"
            matomoClickCategory="Dialog"
            matomoClickAction="Close dialog"
            matomoClickName="Exchange rate calculator"
            mat-flat-button
            mat-dialog-close
            color="accent"
            i18n="@@button-label-close"
            >Close</button
        >
    </mat-dialog-actions>
</ng-template>

<form name="calculatorForm" matomoTrackForm>
    <div class="calculator__inner">
        <div class="row">
            <mat-form-field appearance="outline" color="accent" class="column is-8">
                <mat-label i18n="@@calculator-form-publisher">Exchange rate publisher</mat-label>
                <mat-select
                    name="publisher"
                    matomoTracker
                    #tracker="matomo"
                    matomoCategory="Select"
                    matomoAction="Change publisher"
                    matomoName="{{getSelectedPublisherDescriptionWithCurrencyCode(selectedPublisherId)}}"
                    class="riege-select"
                    [(ngModel)]="selectedPublisherId"
                    (selectionChange)="onPublisherChange($event);tracker.trackEvent();">
                    <mat-option *ngFor="let publisher of publishers" [value]="publisher.id">
                        {{ publisher.description }}
                        <ng-container *ngIf="publisher.targetCurrency as currencyCode">
                            <span>({{ currencyCode }})</span>
                        </ng-container>
                    </mat-option>
                </mat-select>
            </mat-form-field>
        </div>

        <div class="row">
            <mat-form-field appearance="outline" color="accent" class="column is-5">
                <mat-label i18n="@@calculator-form-amount">Amount</mat-label>
                <input
                    matInput
                    name="amount"
                    matomoTracker="change"
                    matomoCategory="Input"
                    matomoAction="Change amount"
                    matomoName="Value"
                    [matomoValue]="sourceAmount"
                    [value]="formattedSourceAmount"
                    (input)="onSourceAmountChange($event)"
                    (keypress)="preventNegativeInput($event)" />
                <span matSuffix class="calculator__suffix">{{ displayedCurrencySuffix }}</span>
            </mat-form-field>

            <ng-container *ngIf="!isReversedConversion">
                <mat-form-field appearance="outline" color="accent" class="column is-3">
                    <mat-label i18n="@@calculator-form-base-currency">From</mat-label>
                    <input matInput [value]="getCurrencyFullNameWithCode(targetCurrency)" disabled />
                </mat-form-field>

                <div class="column is-1 align-center">
                    <button
                        mat-icon-button
                        class="riege-button riege-button--icon calculator__btn--switch"
                        type="button"
                        matomoTrackFormSubmit
                        matomoClickCategory="Button"
                        matomoClickAction="Swap currencies"
                        matomoClickName="Change"
                        (click)="switchCurrencies()"
                        aria-label="Change currencies"
                        i18n-aria-label="@@change-currencies"
                        matTooltip="Change currencies"
                        i18n-matTooltip="@@tooltip-change-currencies"
                        matTooltipPosition="above"
                        [disabled]="isCurrencyInvalid">
                        <mat-icon class="riege-icon">swap_horiz</mat-icon>
                    </button>
                </div>

                <mat-form-field appearance="outline" color="accent" class="column is-3">
                    <mat-label i18n="@@calculator-form-target-currency">To</mat-label>
                    <input
                        matInput
                        type="text"
                        name="to"
                        matomoTracker
                        #tracker="matomo"
                        matomoCategory="Select"
                        matomoAction="To currency"
                        matomoName="{{ sourceCurrencyInput }}"
                        [matAutocomplete]="auto"
                        [formControl]="currency"
                        [(ngModel)]="sourceCurrencyInput"
                        (focus)="onInputFocus()"
                        (ngModelChange)="onCurrencySearchChange($event)"
                        (keydown)="handleKeyDown($event)" />
                    <mat-autocomplete
                        #auto="matAutocomplete"
                        (optionSelected)="onSourceCurrencySelected($event); tracker.trackEvent();">
                        <mat-option *ngFor="let currency of filteredCurrencies" [value]="currency">
                            {{ getCurrencyFullNameWithCode(currency) }}
                        </mat-option>
                    </mat-autocomplete>
                    @if (currency.hasError('invalid')){
                    <mat-hint class="red">{{ errorMessage() }}</mat-hint>
                    }
                </mat-form-field>
            </ng-container>

            <ng-container *ngIf="isReversedConversion">
                <mat-form-field appearance="outline" color="accent" class="column is-3">
                    <mat-label i18n="@@calculator-form-target-currency-switched">From</mat-label>
                    <input
                        matInput
                        type="text"
                        name="from-reverse"
                        matomoTracker
                        #tracker="matomo"
                        matomoCategory="Select"
                        matomoAction="From currency"
                        matomoName="{{ sourceCurrencyInput }}"
                        [matAutocomplete]="auto"
                        [formControl]="currency"
                        [(ngModel)]="sourceCurrencyInput"
                        (focus)="onInputFocus()"
                        (ngModelChange)="onCurrencySearchChange($event);"
                        (keydown)="handleKeyDown($event)" />
                    <mat-autocomplete
                        #auto="matAutocomplete"
                        (optionSelected)="onSourceCurrencySelected($event); tracker.trackEvent();">
                        <mat-option *ngFor="let currency of filteredCurrencies" [value]="currency">
                            {{ getCurrencyFullNameWithCode(currency) }}
                        </mat-option>
                    </mat-autocomplete>
                    @if (currency.hasError('invalid')){
                    <mat-hint class="red">{{ errorMessage() }}</mat-hint>
                    }
                </mat-form-field>

                <div class="column is-1 align-center">
                    <button
                        type="button"
                        mat-icon-button
                        class="riege-button riege-button--icon calculator__btn--switch"
                        matomoTrackFormSubmit
                        matomoClickCategory="Button"
                        matomoClickAction="Swap currencies"
                        matomoClickName="Reverse"
                        (click)="switchCurrencies()"
                        aria-label="Change currencies"
                        i18n-aria-label="@@change-currencies"
                        matTooltip="Change currencies"
                        i18n-matTooltip="@@tooltip-change-currencies"
                        matTooltipPosition="above"
                        [disabled]="isCurrencyInvalid">
                        <mat-icon class="riege-icon">swap_horiz</mat-icon>
                    </button>
                </div>

                <mat-form-field appearance="outline" color="accent" class="column is-3">
                    <mat-label i18n="@@calculator-form-base-currency-switched">To</mat-label>
                    <input matInput [value]="getCurrencyFullNameWithCode(targetCurrency)" disabled />
                </mat-form-field>
            </ng-container>
        </div>

        <div class="row is-multiline is-mobile" [ngClass]="{'margin-top--base-double': currency.hasError('invalid')}">
            <div class="column">
                <button
                    class="riege-button"
                    mat-stroked-button
                    matomoTrackFormSubmit
                    matomoClickCategory="Button"
                    matomoClickAction="Toggle markup"
                    matomoClickName="{{ markupVisible ? 'Remove markup' : 'Add markup' }}"
                    color="accent"
                    (click)="toggleMarkup()">
                    <mat-icon class="riege-icon">{{ markupVisible ? 'remove' : 'add' }}</mat-icon>
                    <span *ngIf="markupVisible" i18n="@@remove-markup-label">Remove markup</span>
                    <span *ngIf="!markupVisible" i18n="@@add-markup-label">Add markup</span>
                </button>
            </div>

            <div class="column markup-spacer"></div>

            <mat-form-field
                *ngIf="markupVisible"
                appearance="outline"
                color="accent"
                class="column is-4-mobile is-3-tablet is-2-desktop">
                <mat-label i18n="@@calculator-form-markup">Markup</mat-label>
                <input
                    matInput
                    name="markup"
                    class="riege-input"
                    matomoTrackFormField
                    matomoTracker="change"
                    matomoCategory="Input"
                    matomoAction="Change markup"
                    matomoName="Value"
                    [matomoValue]="markup"
                    type="number"
                    [(ngModel)]="markup"
                    (ngModelChange)="onMarkupChange($event)"
                    min="0"
                    (keypress)="preventNegativeInput($event)" />
                <span class="calculator__suffix" matSuffix>%</span>
            </mat-form-field>
        </div>

        <div class="calculator__output" *ngIf="!currency.hasError('invalid')">
            <!-- Always visible: Source and Target amounts -->
            <div class="row is-tablet">
                <div class="column label-mobile--small">
                    <div> {{ formatCurrencyAmount(sourceAmount) }} {{ displayedSourceCurrency }} </div>
                </div>

                <div class="column align-right--tablet-up">
                    <div class="font-weight--semi-bold">
                        {{ formatCurrencyAmount(totalWithMarkup) }} {{ displayedTargetCurrency }}
                    </div>
                </div>
            </div>

            <!-- Conditionally visible: Markup fields -->
            <ng-container *ngIf="markupVisible">
                <div class="row is-tablet">
                    <div class="column label-mobile--small">
                        <span i18n="@@markup-in">Markup in</span> {{ displayedTargetCurrency }}
                    </div>
                    <div class="font-weight--semi-bold column align-right--tablet-up">
                        {{ formatCurrencyAmount(markupAmount || 0) }} {{ displayedTargetCurrency }}
                    </div>
                </div>

                <div class="row is-tablet">
                    <div class="column label-mobile--small">
                        <span i18n="@@exchange-rate-with-markup">Exchange rate with markup</span>
                    </div>
                    <div class="font-weight--semi-bold column align-right--tablet-up">
                        <span>1</span> {{ displayedSourceCurrency }} <span>=</span>
                        {{ formatExchangeRate(unitExchangeRateWithMarkup) }} {{ displayedTargetCurrency }}
                    </div>
                </div>
            </ng-container>

            <!-- Always visible: Exchange rate without markup -->
            <div class="row is-tablet">
                <div class="column label-mobile--small">
                    <span i18n="@@exchange-rate-without-markup">Exchange rate without markup</span>
                </div>
                <div class="font-weight--semi-bold column align-right--tablet-up">
                    <span>1</span> {{ displayedSourceCurrency }} <span>=</span>
                    {{ formatExchangeRate(unitExchangeRate) }} {{ displayedTargetCurrency }}
                </div>
            </div>

            <!-- Error handling -->
            <div *ngIf="error">
                <span i18n="@@error-loading-rates">Error loading exchange rates:</span> {{ error.message }}
            </div>
        </div>
    </div>
</form>
